import { useEffect, useState } from 'react';

import { loadPageUpdate } from '../loading/loadPageUpdate';
import { ContentstackLivePreview } from '../setup/initLivePreview';
import { waitForPreviewHash } from '../setup/previewStack';
import { CONTENT_TYPE_POSTFIX, LIVE_PREVIEW_ENABLED } from '../setup/settings';
import { PlainObject } from '../helpers/helpers';

export const useLivePreviewEntry = (
  initialPageData: PlainObject,
  contentTypeBase: string
): PlainObject => {
  const [pageData, setPageData] = useState(initialPageData);

  useEffect(() => {
    if (!LIVE_PREVIEW_ENABLED || !initialPageData?.uid) return undefined;

    if (!CONTENT_TYPE_POSTFIX) {
      console.warn(
        '[live-preview] CONTENTSTACK_CONTENT_TYPE_POSTFIX is not set - live preview is off'
      );

      return undefined;
    }

    let latestRequestId = 0;
    let isMounted = true;

    const refreshFromPreview = async () => {
      latestRequestId += 1;

      const requestId = latestRequestId;
      const isStillNeeded = () => isMounted && requestId === latestRequestId;

      try {
        await waitForPreviewHash();

        if (!isStillNeeded()) return;

        const updatePageData = await loadPageUpdate({
          contentTypeBase,
          entryUid: initialPageData.uid,
          locale: initialPageData.locale,
        });

        if (!updatePageData || !isStillNeeded()) return;

        setPageData(updatePageData(initialPageData));
      } catch (error) {
        if (isStillNeeded()) {
          console.error('[live-preview] entry update failed:', error);
        }
      }
    };

    const subscriptionId =
      ContentstackLivePreview.onEntryChange(refreshFromPreview);

    return () => {
      isMounted = false;
      ContentstackLivePreview.unsubscribeOnEntryChange(subscriptionId);
    };
  }, []);

  return pageData;
};
